import "jspdf-autotable";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import ChampionX from "../assets/img/logo.png";
import { IChartData } from "../modules/device-overview/constants/deviceoverview.interface";
import { parseISO, isValid, format } from "date-fns";

export const downloadExcel = (data: any, name: string) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `${name}`);
};
export const parseDate = (date: string) => {
  if (!date) return null;

  let parsedDate;

  // Try ISO format first
  if (date.includes("T")) {
    parsedDate = parseISO(date);
  } else {
    parsedDate = new Date(date); // Auto-detects various formats
  }

  return isValid(parsedDate) ? parsedDate : null;
};

export const getFormattedDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${month}${day}${year}_${hours}${minutes}${seconds}`;
};
export const downloadPDF = (chartId: string, title: string) => {
  const input = document.getElementById(chartId);
  if (input) {
    const originalBackground = input.style.background;
    input.style.background = "#131F2E";
    const originalHeight = input.style.height;
    const originalOverflow = input.style.overflow;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: document.documentElement.scrollHeight,
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
          putOnlyUsedFonts: true,
          floatPrecision: 16,
        });

        const imgWidth = 190;
        const pageHeight = pdf.internal.pageSize.height;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 40;

        const addCustomHeader = (pdf: any) => {
          pdf.setFillColor(1, 72, 94);
          pdf.rect(0, 0, pdf.internal.pageSize.width, 15, "F");

          const imgWidth = 30;
          const imgHeight = 5;
          pdf.addImage(ChampionX, "PNG", 5, 5, imgWidth, imgHeight);

          pdf.setFontSize(12);
          pdf.setTextColor("#fff");
          pdf.text(title, 95, 9);

          pdf.setFontSize(12);
          pdf.setTextColor("#fff");
          pdf.text("RDM", 185, 9);
        };

        addCustomHeader(pdf);

        pdf.setFontSize(16);
        pdf.setTextColor("#000");
        pdf.text(title, 75, 25);

        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          pdf.addPage();
          addCustomHeader(pdf);
          position = 40;
          pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          pdf.setFontSize(9);
          pdf.setTextColor("#000");
          pdf.text(`Generated By: Daniyal Amjad`, 10, pageHeight - 10);
          pdf.text(
            `Generated on: ${new Date().toLocaleDateString()}`,
            150,
            pageHeight - 10,
          );
        }

        const filename = generateFileName(title, "pdf");
        pdf.save(filename);

        input.style.height = originalHeight;
        input.style.overflow = originalOverflow;
      })
      .catch((error) => {
        console.error("Error capturing the chart:", error);
        input.style.background = originalBackground;
      });
  } else {
    console.error("Chart element not found!");
  }
};

export const totalSum = (total: IChartData[]) => {
  return total.reduce(
    (sum: number, item: { devices: number }) => sum + item.devices,
    0,
  );
};

const timeZoneAliases: Record<string, string> = {
  "Asia/Calcutta": "Asia/Kolkata",
  "US/Eastern": "America/New_York",
  "US/Central": "America/Chicago",
  "US/Mountain": "America/Denver",
  "US/Pacific": "America/Los_Angeles",

  GMT: "Etc/GMT",
  UTC: "Etc/UTC",
  // Add more if needed
};

export const formatDateTime = (text: string, timezone?: string): string => {
  if (!text || text === "N/A") return "N/A";

  const parsedDate = new Date(text); // assumes UTC input

  if (isNaN(parsedDate.getTime())) return text;

  let effectiveTimeZone =
    timezone ||
    (typeof window !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "UTC");

  // Normalize alias to modern IANA time zone
  if (timeZoneAliases[effectiveTimeZone]) {
    effectiveTimeZone = timeZoneAliases[effectiveTimeZone];
  }

  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: effectiveTimeZone,
  };

  try {
    const formatted = parsedDate
      .toLocaleString(undefined, options)
      .replace(",", "") // Remove comma
      .replace(/\s(AM|PM)/, (match) => match.toLowerCase()); // lowercase am/pm

    return formatted;
  } catch (err) {
    console.warn(
      "Invalid time zone:",
      effectiveTimeZone,
      "Falling back to UTC",
    );
    return parsedDate
      .toLocaleString(undefined, { ...options, timeZone: "UTC" })
      .replace(",", "")
      .replace(/\s(AM|PM)/, (match) => match.toLowerCase());
  }
};

export const Masterunits = [
  "%",
  "A",
  "V",
  "Hz",
  "kW",
  "kvar",
  "psi",
  "F",
  "Mohm",
  "Vdc",
  "bpd",
  "ft",
  "g",
  "s",
  "ms",
  "min",
  "hour",
  "in",
  "W",
  "kHz",
  "N/A",
  "kVA",
  "hp",
  "rpm",
];

export interface ITimeCustom {
  text: string | null | undefined;
  timezone?: string;
}

export const formatDateTimeCustom = (
  text: string | null | undefined,
  timezone = "Asia/Colombo", // Default to Asia/Colombo if no timezone is passed
): string => {
  try {
    // Trim and check for invalid or "N/A" strings
    const raw = (text ?? "").trim();
    if (!raw || raw === "N/A") {
      return "N/A";
    }

    // ---- Parse the Date ----
    let date: Date;

    // Handle different formats
    if (/^\d+$/.test(raw)) {
      // Numeric epoch (seconds or milliseconds)
      const timestamp = Number(raw);
      date = new Date(raw.length <= 10 ? timestamp * 1000 : timestamp);
    } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(raw)) {
      // ISO 8601 (with or without Z/offset)
      date = new Date(raw);
    } else if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(raw)) {
      // MM/DD/YYYY HH:mm:ss format
      const [mm, dd, yyyy, hh, min, sec] = raw.split(/[/ :]/).map(Number);
      date = new Date(yyyy, mm - 1, dd, hh, min, sec);
    } else if (/^\d{2} \w{3,9} \d{4} \d{2}:\d{2} (AM|PM)$/i.test(raw)) {
      // "09 May 2024 01:36 PM" format
      const match = raw.match(
        /^(\d{2}) (\w{3,9}) (\d{4}) (\d{2}):(\d{2}) (AM|PM)$/i,
      );
      if (match) {
        const [, dd, monthStr, yyyy, hh, min, ampm] = match;
        const monthIndex = new Date(`${monthStr} 1, 2000`).getMonth();
        let hour = Number(hh);
        if (/pm/i.test(ampm) && hour !== 12) hour += 12;
        if (/am/i.test(ampm) && hour === 12) hour = 0;
        date = new Date(
          Number(yyyy),
          monthIndex,
          Number(dd),
          hour,
          Number(min),
        );
      } else {
        return raw; // Return as-is if not recognized
      }
    } else {
      // Last-resort parse using the built-in Date constructor
      date = new Date(raw);
    }

    // Ensure valid date
    if (isNaN(date.getTime())) {
      console.error("Invalid Date:", raw);
      return "Invalid Date";
    }

    // ---- Adjust Time Zone (Asia/Colombo) ----
    const adjustedDate = new Date(
      date.toLocaleString("en-US", { timeZone: timezone }),
    );

    // ---- Format the Date ----
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      weekday: "short", // Optional: weekday (e.g., Tue)
      day: "2-digit", // Show day as two digits
      month: "short", // Show abbreviated month (e.g., Oct)
      year: "numeric", // Full year (e.g., 2025)
      hour: "2-digit", // Show hour as two digits (12-hour format)
      minute: "2-digit", // Show minute as two digits
      second: "2-digit", // Show second as two digits
      hour12: true, // Use 12-hour clock (AM/PM)
    };

    const formattedParts = new Intl.DateTimeFormat(
      "en-GB",
      options,
    ).formatToParts(adjustedDate);

    // Extract individual components from formatted date
    const day = formattedParts.find((part) => part.type === "day")?.value;
    const month = formattedParts.find((part) => part.type === "month")?.value;
    const year = formattedParts.find((part) => part.type === "year")?.value;
    const hour = formattedParts.find((part) => part.type === "hour")?.value;
    const minute = formattedParts.find((part) => part.type === "minute")?.value;
    const second = formattedParts.find((part) => part.type === "second")?.value;
    const dayPeriod = formattedParts
      .find((part) => part.type === "dayPeriod")
      ?.value.toLocaleUpperCase(); // AM/PM

    // Construct the final formatted string
    return `${day} ${month} ${year}, ${hour}:${minute}:${second} ${dayPeriod}`.trim();
  } catch (error) {
    console.error("Error in formatting date:", error);
    return "Invalid Date";
  }
};

export const formatDateTimeWithoutSeconds = (
  text: string,
  timezone?: string,
): string => {
  if (!text || text === "N/A") return "N/A";

  const parsedDate = new Date(text); // assumes UTC input

  if (isNaN(parsedDate.getTime())) return text;

  let effectiveTimeZone =
    timezone ||
    (typeof window !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "UTC");

  // Normalize alias to modern IANA time zone
  if (timeZoneAliases[effectiveTimeZone]) {
    effectiveTimeZone = timeZoneAliases[effectiveTimeZone];
  }

  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: effectiveTimeZone,
  };

  try {
    const formatted = parsedDate
      .toLocaleString("en-GB", options)
      .replace(/,/g, "")
      .replace(/\s(am|pm)/i, (match) => match.toUpperCase()) // uppercase AM/PM
      .replace(/(\d{2})\s(\w{3})\s(\d{4})\s(.+)/, "$1 $2 $3, $4");
    return formatted;
  } catch (err) {
    console.warn(
      "Invalid time zone:",
      effectiveTimeZone,
      "Falling back to UTC",
    );
    return parsedDate
      .toLocaleString("en-GB", { ...options, timeZone: "UTC" })
      .replace(/,/g, "")
      .replace(/\s(am|pm)/i, (match) => match.toUpperCase())
      .replace(/(\d{2})\s(\w{3})\s(\d{4})\s(.+)/, "$1 $2 $3, $4");
  }
};

// Add a utility function to render values with conditional styling for N/A only
export const renderCellValue = (value: string) => {
  if (value === "N/A") {
    return <span className="text-neutral-400 italic">{value}</span>;
  }
  return <span className="font-semibold">{value}</span>;
};

//Global search for date fields throughout the application
export const searchInText = (searchTerm: string, text: string): boolean => {
  if (!searchTerm || !text) return true;

  // Convert both to lowercase for case-insensitive search
  const normalizedSearchTerm = searchTerm?.toLowerCase().trim();
  const normalizedText = text.toLowerCase();

  // Split search term into words
  const searchWords = normalizedSearchTerm
    .split(/\s+/)
    .filter((word) => word.length > 0);

  // If no valid words, return true
  if (searchWords.length === 0) return true;

  // Check if all words are present in the text (AND logic)
  return searchWords.every((word) => normalizedText.includes(word));
};

/**
 * Generates a file name in the format: (Table_Name)_mmddyyyy_hhmmss.ext
 * @param tableName - The base name of the table/file
 * @param extension - The file extension (e.g., 'xls', 'csv', 'pdf')
 * @returns The formatted file name string
 */
export const generateFileName = (
  tableName: string,
  extension: string,
): string => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  // Replace blanks with underscore and capitalize first letter
  const formattedTableName = tableName
    .replace(/\s+/g, "_")
    .replace(/^([a-z])/, (m) => m.toUpperCase());
  return `${formattedTableName}_${mm}${dd}${yyyy}_${hh}${min}${ss}.${extension}`;
};

export const convertSeconds = (seconds: number): string => {
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  } else if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  } else {
    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  }
};

export const isTimestampPassed = (
  createdTime: number,
  durationInSeconds: number,
): boolean => {
  return createdTime + durationInSeconds * 1000 < Date.now();
};

export const sortArray = (array: any[], sortBy: string, sortOrder: string) => {
  if (!sortBy || sortOrder === "none") return array;

  const list = [...array].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortOrder === "asc" ? 1 : -1;
    if (bValue == null) return sortOrder === "asc" ? -1 : 1;

    // Special handling for specific columns
    switch (sortBy) {
      case "pollingInterval":
      case "polling":
        // Convert to numbers for proper numeric sorting
        const numA = parseFloat(aValue) || 0;
        const numB = parseFloat(bValue) || 0;
        return sortOrder === "asc" ? numA - numB : numB - numA;

      case "unit":
      case "units":
        // Handle "N/A" values and empty strings
        const unitA = (aValue || "").toString().toLowerCase();
        const unitB = (bValue || "").toString().toLowerCase();

        // Put N/A values at the end
        if (unitA === "n/a" && unitB !== "n/a")
          return sortOrder === "asc" ? 1 : -1;
        if (unitB === "n/a" && unitA !== "n/a")
          return sortOrder === "asc" ? -1 : 1;
        if (unitA === "n/a" && unitB === "n/a") return 0;

        // Regular string comparison for other values
        return sortOrder === "asc"
          ? unitA.localeCompare(unitB)
          : unitB.localeCompare(unitA);

      case "decimalplace":
      case "decimalPlaces":
        // Numeric sorting for decimal places
        const decA = parseInt(aValue) || 0;
        const decB = parseInt(bValue) || 0;
        return sortOrder === "asc" ? decA - decB : decB - decA;

      default:
        // Default string sorting for other columns
        const strA = aValue.toString().toLowerCase();
        const strB = bValue.toString().toLowerCase();
        return sortOrder === "asc"
          ? strA.localeCompare(strB)
          : strB.localeCompare(strA);
    }
  });
  return sortArrayMethod(list, sortBy, sortOrder);
};

export const sortArrayForSerialNumber = <T extends Record<string, any>>(
  array: T[],
  key: keyof T | string,
  order: "asc" | "desc" | "none",
): T[] => {
  if (!key || order === "none") return array;

  const isPureNumber = (value: string) => /^\d+$/.test(value);
  const isAlpha = (value: string) => /^[a-zA-Z]+$/.test(value);

  return [...array].sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    if (valA == null) return order === "asc" ? -1 : 1;
    if (valB == null) return order === "asc" ? 1 : -1;

    if (typeof valA === "string" && typeof valB === "string") {
      const typeA = isPureNumber(valA) ? 0 : isAlpha(valA) ? 1 : 2;
      const typeB = isPureNumber(valB) ? 0 : isAlpha(valB) ? 1 : 2;

      // Sort by type group: numbers < alpha < alphanum
      if (typeA !== typeB) {
        return order === "asc" ? typeA - typeB : typeB - typeA;
      }

      // Sort within same type group using string comparison
      return order === "asc"
        ? valA.localeCompare(valB, undefined, { sensitivity: "base" })
        : valB.localeCompare(valA, undefined, { sensitivity: "base" });
    }

    // Fallbacks for other types
    const dateA = new Date(valA);
    const dateB = new Date(valB);
    if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
      return order === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    if (typeof valA === "boolean" && typeof valB === "boolean") {
      return order === "asc"
        ? valA === valB
          ? 0
          : valA
            ? 1
            : -1
        : valA === valB
          ? 0
          : valA
            ? -1
            : 1;
    }

    if (typeof valA === "number" && typeof valB === "number") {
      return order === "asc" ? valA - valB : valB - valA;
    }

    return 0;
  });
};

/**
 * Filters a list by checking specific keys against a search term.
 * Supports case-insensitive exact match or partial match.
 *
 * @param list - The array of objects to search
 * @param searchText - The search string
 * @param keys - The keys in each object to check
 * @param exactMatch - Whether to match exactly (true) or do partial search (false)
 * @returns Filtered array
 */
export const filterListByKeys = (
  list: any[],
  searchText: string,
  keys: string[],
  exactMatch = false,
): any[] => {
  if (!searchText || keys.length === 0) return list;

  const normalizedSearch = searchText?.toLowerCase();

  return list.filter((item) =>
    keys.some((key) => {
      const value = item[key]?.toString().toLowerCase() || "";

      return exactMatch
        ? value === normalizedSearch // exact match, case-insensitive
        : value.includes(normalizedSearch); // partial match
    }),
  );
};

export const addUserObject = (data: any) => {
  if (data?._id) {
    return {
      ...data,
      updatedBy: localStorage.getItem("userId") || "",
      lastUpdated: new Date().toISOString(),
    };
  } else {
    return {
      ...data,
      createdBy: localStorage.getItem("userId") || "",
      createdOn: new Date().toISOString(),
      updatedBy: localStorage.getItem("userId") || "",
      lastUpdated: new Date().toISOString(),
    };
  }
};

export const convertDate = (dateString: string): string => {
  const date = new Date(dateString);
  return format(date, "dd MMMM yyyy, hh:mm:ss a");
};

export const sortArrayMethod = <T extends Record<string, any>>(
  array: T[],
  key: keyof T | string,
  order: "asc" | "desc" | "none" | string,
): T[] => {
  if (!key || order === "none") return array; // Return the original array if no sorting is needed

  const sortedArray = [...array].sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    // Handle null or undefined values
    if (valA == null) return -1;
    if (valB == null) return 1;

    // Handle date values
    const dateA = new Date(valA);
    const dateB = new Date(valB);

    if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
      return dateA.getTime() - dateB.getTime();
    }

    // Handle boolean values
    if (typeof valA === "boolean" && typeof valB === "boolean") {
      return valA === valB ? 0 : valA ? 1 : -1;
    }

    // Handle string values
    if (typeof valA === "string" && typeof valB === "string") {
      return valA.localeCompare(valB, undefined, { sensitivity: "base" }); // Case-insensitive ascending
    }

    // Handle number values
    if (typeof valA === "number" && typeof valB === "number") {
      return valA - valB;
    }

    return 0; // If types don't match, keep original order
  });

  // Reverse the array if the order is "desc"
  return order === "desc" ? sortedArray.reverse() : sortedArray;
};

// Convert Celsius to Fahrenheit
export const celsiusToFahrenheit = (celsius: number): number => {
  if (typeof celsius !== "number") {
    return 0;
  }
  return (celsius * 9) / 5 + 32;
};

// Convert Fahrenheit to Celsius
export const fahrenheitToCelsius = (fahrenheit: number): number => {
  if (typeof fahrenheit !== "number") {
    return 0;
  }
  return ((fahrenheit - 32) * 5) / 9;
};

export function convertToUTC(localDateString: string): string {
  const localDate = new Date(localDateString);
  if (isNaN(localDate.getTime())) {
    throw new Error("Invalid date string");
  }
  return localDate.toISOString();
}

export const formatDateTimeUTC = (text: string): string => {
  try {
    if (!text || text === "N/A") return "N/A";

    const date = new Date(text);
    if (isNaN(date.getTime())) return "Invalid Date";

    const formatter = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "UTC", // Force UTC output
    });

    return formatter
      .format(date)
      .replace(/,/g, "")
      .replace(/\s(AM|PM)/i, (match) => match.toUpperCase())
      .replace(/(\d{2})\s(\w{3})\s(\d{4})\s(.+)/, "$1 $2 $3, $4");
  } catch {
    return "Invalid Date";
  }
};

export const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function formatDateToHourAndMinute(
  dateString: string | null | undefined,
): string {
  if (!dateString) return "N/A";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "N/A";
  }

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Ensure 2-digit minutes
  const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;

  return `${month}/${day}/${year} ${hours}:${formattedMinutes}`;
}

export const downloadJson = (data: any, filename: string) => {
  const fileData = JSON.stringify(data, null, 2);
  const blob = new Blob([fileData], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}`;
  link.click();
  URL.revokeObjectURL(url);
};
